import { Logo } from '@/shared/components';
import { useServices } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import { useI18n } from '@/shared/i18n';
import { useNavBadges } from '@/shared/hooks/useNavBadges';
import { ADMIN_V2_SECTIONS } from '@/shared/navigation/admin-v2-config';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarSection } from './SidebarSection';
import { TenantBadge } from './TenantBadge';
import { UserDropdown } from './UserDropdown';

const COLLAPSED_KEY = 'hommi.sidebar.collapsed';
const OPEN_SECTION_KEY = 'hommi.sidebar.openSection';

const hasItemPermission = (
  ability: ReturnType<typeof useAbility>,
  item: (typeof ADMIN_V2_SECTIONS)[number]['items'][number],
) => {
  if (!item.permission) return true;
  if (ability.can(item.permission.action, item.permission.subject as any)) return true;
  if (ability.can('manage', 'all')) return true;
  return false;
};

function getActiveId(pathname: string): string {
  if (pathname === '/admin-v2') return 'dashboard';
  if (pathname.startsWith('/admin-v2/cuotas')) return 'cuotas';
  if (pathname.startsWith('/admin-v2/proximamente')) return '';
  if (pathname.startsWith('/admin/residentes')) return 'residentes';
  if (pathname.startsWith('/admin/unidades')) return 'unidades';
  if (pathname.startsWith('/admin/secciones')) return 'secciones';
  if (pathname.startsWith('/admin/reservas')) return 'reservas';
  if (pathname.startsWith('/admin/comunicados')) return 'avisos';
  if (pathname.startsWith('/encuestas')) return 'votaciones';
  if (pathname.startsWith('/admin/transparencia')) return 'transparencia';
  if (pathname.startsWith('/accesos')) return 'accesos';
  if (pathname.startsWith('/incidentes')) return 'incidentes';
  if (pathname.startsWith('/camaras')) return 'camaras';
  if (pathname.startsWith('/reportes')) return 'reportes';
  if (pathname.startsWith('/admin/personal')) return 'personal';
  if (pathname.startsWith('/admin/roles')) return 'roles';
  if (pathname.startsWith('/proveedores')) return 'proveedores';
  if (pathname.startsWith('/admin/finanzas')) return 'finanzas';
  if (pathname.startsWith('/admin/resumen-finanzas')) return 'resumen-finanzas';
  if (pathname.startsWith('/admin/categorias')) return 'categorias';
  if (pathname.startsWith('/admin/amenidades')) return 'amenidades-reg';
  if (pathname.startsWith('/admin/facturacion')) return 'pagos';
  if (pathname.startsWith('/admin/egresos')) return 'gastos';
  if (pathname.startsWith('/cobranza')) return 'cobranza';
  if (pathname.startsWith('/registro-accesos')) return 'visitas';
  if (pathname.startsWith('/admin/puntos-acceso')) return 'puntos-acceso';
  if (pathname.startsWith('/admin/tarjetas-rfid')) return 'rfid';
  if (pathname.startsWith('/documentos')) return 'documentos';
  if (pathname.startsWith('/admin/routines')) return 'routines';
  if (pathname.startsWith('/admin/notificaciones-config')) return 'notificaciones-config';
  if (pathname.startsWith('/admin/auditoria')) return 'auditoria';
  if (pathname.startsWith('/admin/configuracion')) return 'configuracion';
  return '';
}

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const { systemSettings } = useServices();
  const ability = useAbility();
  const { t } = useI18n();
  const location = useLocation();
  const activeId = getActiveId(location.pathname);
  const badges = useNavBadges();
  const visibleSections = ADMIN_V2_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => hasItemPermission(ability, item)),
  })).filter((section) => section.items.length > 0);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);

  const sectionContainingActive = visibleSections.find((s) =>
    s.items.some((i) => i.id === activeId),
  )?.id ?? null;

  const [openSectionId, setOpenSectionId] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(OPEN_SECTION_KEY);
      if (stored !== null) return stored === '' ? null : stored;
    } catch {
      // ignore
    }
    return (
      visibleSections.find((s) => s.items.some((i) => i.id === activeId))?.id ??
      visibleSections.find((s) => s.defaultOpen)?.id ??
      visibleSections[0]?.id ??
      null
    );
  });

  const prevActiveSectionRef = useRef(sectionContainingActive);
  useEffect(() => {
    if (
      sectionContainingActive &&
      sectionContainingActive !== prevActiveSectionRef.current
    ) {
      setOpenSectionId(sectionContainingActive);
    }
    prevActiveSectionRef.current = sectionContainingActive;
  }, [sectionContainingActive]);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_SECTION_KEY, openSectionId ?? '');
    } catch {
      // ignore
    }
  }, [openSectionId]);

  const toggleSection = useCallback((id: string) => {
    setOpenSectionId((current) => (current === id ? null : id));
  }, []);

  const logoUrl = systemSettings?.branding?.logoUrl;
  const width = collapsed ? 'md:w-[72px]' : 'md:w-64';

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden backdrop-blur-sm"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-30 w-64 sidebar-glass transform transition-[transform,width] duration-300 ease-out
          md:relative md:translate-x-0 ${width}
          flex flex-col h-screen
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo + collapse toggle */}
        <div className={`flex items-center border-b border-theme-light gap-2 ${
          collapsed ? 'px-2 py-3 justify-center' : 'px-4 pt-5 pb-3 justify-between'
        }`}>
          {!collapsed && (
            <div className="flex items-center flex-1 min-w-0">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-12 w-full object-contain object-left" />
              ) : (
                <Logo size="sidebar" className="object-left" />
              )}
            </div>
          )}

          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? t('menu.v2.sidebar.expand') : t('menu.v2.sidebar.collapse')}
            title={collapsed ? t('menu.v2.sidebar.expand') : t('menu.v2.sidebar.collapse')}
            className="hidden md:flex p-1.5 rounded-lg hover:bg-surface-secondary text-theme-tertiary hover:text-theme transition-colors"
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>

          <button
            type="button"
            className="md:hidden p-1.5 rounded-lg hover:bg-surface-secondary text-theme-secondary"
            onClick={onMobileClose}
          >
            <X size={18} />
          </button>
        </div>

        {/* Tenant badge */}
        <div className={`border-b border-theme-light ${collapsed ? 'px-1' : 'px-2 py-2'}`}>
          <TenantBadge collapsed={collapsed} />
        </div>

        {/* Navigation */}
        <div className={`flex-1 overflow-y-auto overflow-x-hidden ${collapsed ? 'px-2 py-3' : 'px-2 py-3'}`}>
          <nav>
            {visibleSections.map((section) => (
              <SidebarSection
                key={section.id}
                section={section}
                activeId={activeId}
                badges={badges}
                collapsed={collapsed}
                isOpen={openSectionId === section.id}
                onToggle={toggleSection}
                onItemClick={onMobileClose}
              />
            ))}
          </nav>
        </div>

        {/* Footer */}
        <div className={`border-t border-theme-light ${collapsed ? 'px-1 py-2' : 'px-2 pb-3 pt-2'}`}>
          <UserDropdown collapsed={collapsed} />
        </div>
      </aside>
    </>
  );
}
